import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import { useStrings } from '@/i18n/use-strings';
import { Icon } from './icon';
import { ScrollArea } from './scroll-area';

/**
 * The app's dialog. It was the registry copy with a bottom-sheet override on
 * it; ticket 23 §4 gives it the same language as everything else the app puts
 * over the page — the sheets' scrim, a 1.5 rem radius, the header's icon
 * button as the close affordance, and the ticket-11 duration and easing
 * tokens, which are the CSS mirror of `components/motion/tokens.ts`.
 *
 * The enter and exit stay CSS on Radix's `data-state` rather than moving to
 * `motion`: the API has to stay exactly what it is (six files import these),
 * and `DialogContent` has no access to `Dialog.Root`'s open state, so an
 * `AnimatePresence` here would mean either a new required prop or a second
 * source of truth for "is it open". `--bp-ease-out` is `EASE.out` to the same
 * four decimals, so the curve is the one the rest of the app arrives on.
 */

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/**
 * The scrim. `black/50` is `SheetOverlay`'s value, so a dialog and a sheet sit
 * on the same ground, plus a small static blur to separate the surface from a
 * busy photograph. Static: set once when it mounts, never animated, so it
 * costs one composite and nothing per frame.
 */
function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
        'duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)] data-[state=closed]:duration-[var(--bp-duration-fast)]',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({ className, children, ...props }: React.ComponentProps<typeof DialogPrimitive.Content>) {
  const { header } = useStrings();

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'text-bp-ink data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed z-50 grid gap-4 overscroll-contain bg-white',
          // The app's elevation: a wide, soft navy shadow rather than the
          // registry's neutral `shadow-lg`, and no border — the shadow is what
          // separates the surface from the page, so a hairline on top of it
          // only muddies the edge.
          'shadow-[0_-8px_40px_-12px_hsl(var(--surf-dark-blue)/0.45)] sm:shadow-[0_32px_80px_-34px_hsl(var(--surf-dark-blue)/0.85)]',
          'duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)] data-[state=closed]:duration-[var(--bp-duration-fast)] data-[state=closed]:ease-[var(--bp-ease-in)]',
          /*
            A bottom sheet on a phone, a centred card from `sm` up.
            A dialog dropped in the middle of a 390 px screen is a web
            convention, not a phone one: its buttons land where no thumb
            reaches and it reads as an interruption. Anchored to the bottom
            edge, sliding up, with the home indicator's inset added, it reads
            the way every native confirmation does — and it is the same
            component, so nothing renders twice.

            `inset-x-0` and the `-translate-x` centring are both physical on
            purpose: they are symmetric, so they are already correct in RTL.
            Everything asymmetric inside uses logical sides.
          */
          'inset-x-0 bottom-0 w-full rounded-t-3xl pb-[env(safe-area-inset-bottom,0px)]',
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          'sm:inset-x-auto sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:max-w-lg sm:-translate-x-[50%] sm:-translate-y-[50%] sm:rounded-3xl sm:pb-0',
          'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
          // Reduced motion keeps the fade and drops the travel: the surface
          // appears rather than arriving. `bp-overlay` is the same rule
          // `SheetContent` carries; see `app.css` for why it is a class and
          // two attributes rather than `motion-reduce:[--tw-*]`.
          'bp-overlay',
          className,
        )}
        {...props}
      >
        <ScrollArea className="max-h-[85dvh] sm:max-h-[calc(100dvh-3rem)]" data-slot="dialog-content-scroll-area">
          <div className="flex flex-col gap-4 p-5 sm:p-6" data-slot="dialog-content-inner">
            {children}
          </div>
        </ScrollArea>

        {/*
          The header's icon button, to the class: 44 px, blue on a blue wash, a
          4 px blue focus ring. It used to be a 16 px glyph at 70 % opacity
          with a square focus ring — the one control in the app that looked
          like it came from somewhere else.

          It is a sibling of the scroller, not the last thing inside it. Inside,
          it was absolutely positioned against the surface but still part of the
          scrolled content, so on a dialog long enough to scroll the only way
          out of it scrolled away with the copy. `z-10` keeps it over the
          sticky footer below.
        */}
        <DialogPrimitive.Close className="text-surf-blue hover:bg-surf-blue/[0.08] focus-visible:ring-surf-blue/40 absolute end-3 top-3 z-10 grid size-11 place-items-center rounded-full transition-[background-color,scale] duration-150 focus-visible:ring-4 focus-visible:outline-none active:scale-96 disabled:pointer-events-none motion-reduce:transition-none">
          <Icon name="passport/close" aria-hidden className="size-5" />
          <span className="sr-only">{header.header_close}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/**
 * `pe-11` is the close button's clearance. It sits on the header rather than
 * on the content column, because the title is the only thing that reaches far
 * enough to collide with it — insetting the footer as well would leave the
 * full-width actions short of the surface's own edge.
 */
function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex flex-col gap-2 pe-11 text-start', className)} {...props} />;
}

/**
 * The actions. Pinned to the bottom of the scroller rather than sitting at the
 * end of the copy: a dialog whose body outgrows the surface used to put its
 * buttons below the fold, which on a phone is the same as not having any.
 * Sticky costs nothing when the content is short — it is already at the bottom
 * — so there is one behaviour rather than two.
 *
 * The white ground and the negative gutters are what make it opaque all the
 * way to the surface's edges as the copy passes underneath; the hairline is
 * what says the copy continues above it.
 */
function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'sticky bottom-0 z-0 -mx-5 mt-auto -mb-5 flex flex-col-reverse gap-2 border-t border-black/[0.08] bg-white px-5 pt-4 pb-5 sm:-mx-6 sm:-mb-6 sm:flex-row sm:justify-end sm:px-6 sm:pb-6',
        className,
      )}
      {...props}
    />
  );
}

/** The brand display face, as every other surface heading in the app sets it. */
function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-bebas text-surf-dark-blue text-3xl leading-none tracking-wide uppercase', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-bp-ink-muted text-base leading-relaxed', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
